/**
 * Mock Policy Violations Data
 * 
 * This file contains mock data for policy violations based on working patterns.
 * Violations are calculated based on each employee's expected hours from their working profile.
 */

import type { PolicyCheckType } from '@/types/timesheetSettings';

export interface PolicyViolation {
  id: string;
  employeeId: string;
  employeeName: string;
  weekEnding: string;
  violationType: PolicyCheckType;
  severity: 'info' | 'warning' | 'error';
  message: string;
  details: {
    expected: number;
    actual: number;
    threshold?: number;
    limit?: number;
  };
  date?: string; // For daily violations
}

/**
 * Mock policy violations for the current week
 * These are based on realistic scenarios for different employee working patterns
 */
export const mockPolicyViolations: PolicyViolation[] = [
  // Sarah Chen - Daily Overage (Standard 40h/week, worked 10.5h on Monday)
  {
    id: 'pv-001',
    employeeId: 'emp-001',
    employeeName: 'Sarah Chen',
    weekEnding: '2024-01-19',
    date: '2024-01-15',
    violationType: 'daily-overage',
    severity: 'warning',
    message: 'Daily hours exceed threshold',
    details: {
      expected: 8.0,
      actual: 10.5,
      threshold: 10.0, // 125% of 8h
    },
  },
  
  // Michael Rodriguez - Weekly Overage (Standard 40h/week, worked 46h)
  {
    id: 'pv-002',
    employeeId: 'emp-002',
    employeeName: 'Michael Rodriguez',
    weekEnding: '2024-01-19',
    violationType: 'weekly-overage',
    severity: 'warning',
    message: 'Weekly hours exceed threshold',
    details: {
      expected: 40.0,
      actual: 46.0,
      threshold: 44.0, // 110% of 40h
    },
  },
  
  // Emily Watson - Daily Overage (Part-time 20h/week, worked 9h on Wednesday)
  {
    id: 'pv-003',
    employeeId: 'emp-003',
    employeeName: 'Emily Watson',
    weekEnding: '2024-01-19',
    date: '2024-01-17',
    violationType: 'daily-overage',
    severity: 'warning',
    message: 'Daily hours exceed threshold',
    details: {
      expected: 6.67, // 20h / 3 days
      actual: 9.0,
      threshold: 8.34, // 125% of 6.67h
    },
  },
  
  // David Kim - Legal Maximum Daily (Compressed 4x10, worked 13.5h)
  {
    id: 'pv-004',
    employeeId: 'emp-004',
    employeeName: 'David Kim',
    weekEnding: '2024-01-19',
    date: '2024-01-16',
    violationType: 'legal-daily-maximum',
    severity: 'error',
    message: 'Daily hours exceed legal maximum',
    details: {
      expected: 10.0,
      actual: 13.5,
      limit: 13.0,
    },
  },
  
  // Jessica Martinez - Missing Hours (Standard 40h/week, only worked 28h)
  {
    id: 'pv-005',
    employeeId: 'emp-005',
    employeeName: 'Jessica Martinez',
    weekEnding: '2024-01-19',
    violationType: 'missing-hours',
    severity: 'info',
    message: 'Weekly hours below expected',
    details: {
      expected: 40.0,
      actual: 28.0,
      threshold: 32.0, // 80% of 40h
    },
  },
  
  // James Wilson - Weekly Overage (Standard 40h/week, worked 52h)
  {
    id: 'pv-006',
    employeeId: 'emp-006',
    employeeName: 'James Wilson',
    weekEnding: '2024-01-19',
    violationType: 'weekly-overage',
    severity: 'error',
    message: 'Weekly hours significantly exceed threshold',
    details: {
      expected: 40.0,
      actual: 52.0,
      threshold: 44.0, // 110% of 40h
    },
  },
  
  // Lisa Anderson - Daily Overage (UK Standard 37.5h/week, worked 11h)
  {
    id: 'pv-007',
    employeeId: 'emp-007',
    employeeName: 'Lisa Anderson',
    weekEnding: '2024-01-19',
    date: '2024-01-18',
    violationType: 'daily-overage',
    severity: 'warning',
    message: 'Daily hours exceed threshold',
    details: {
      expected: 7.5,
      actual: 11.0,
      threshold: 9.38, // 125% of 7.5h
    },
  },
  
  // Robert Taylor - Legal Maximum Weekly (Standard 40h/week, worked 50h)
  {
    id: 'pv-008',
    employeeId: 'emp-008',
    employeeName: 'Robert Taylor',
    weekEnding: '2024-01-19',
    violationType: 'legal-weekly-maximum',
    severity: 'error',
    message: 'Weekly hours exceed legal maximum',
    details: {
      expected: 40.0,
      actual: 50.0,
      limit: 48.0,
    },
  },
  
  // Amanda White - Missing Hours (Part-time 16h/week, only worked 10h)
  {
    id: 'pv-009',
    employeeId: 'emp-009',
    employeeName: 'Amanda White',
    weekEnding: '2024-01-19',
    violationType: 'missing-hours',
    severity: 'info',
    message: 'Weekly hours below expected',
    details: {
      expected: 16.0,
      actual: 10.0,
      threshold: 12.8, // 80% of 16h
    },
  },
  
  // Christopher Brown - Daily Overage (4-day week 32h, worked 10h)
  {
    id: 'pv-010',
    employeeId: 'emp-010',
    employeeName: 'Christopher Brown',
    weekEnding: '2024-01-19',
    date: '2024-01-17',
    violationType: 'daily-overage',
    severity: 'warning',
    message: 'Daily hours exceed threshold',
    details: {
      expected: 8.0,
      actual: 10.0,
      threshold: 10.0, // 125% of 8h
    },
  },
  
  // Jennifer Davis - Weekly Overage (Standard 40h/week, worked 48h)
  {
    id: 'pv-011',
    employeeId: 'emp-011',
    employeeName: 'Jennifer Davis',
    weekEnding: '2024-01-19',
    violationType: 'weekly-overage',
    severity: 'warning',
    message: 'Weekly hours exceed threshold',
    details: {
      expected: 40.0,
      actual: 48.0,
      threshold: 44.0, // 110% of 40h
    },
  },
  
  // Daniel Miller - Daily Overage (Extended 45h/week, worked 12h)
  {
    id: 'pv-012',
    employeeId: 'emp-012',
    employeeName: 'Daniel Miller',
    weekEnding: '2024-01-19',
    date: '2024-01-15',
    violationType: 'daily-overage',
    severity: 'warning',
    message: 'Daily hours exceed threshold',
    details: {
      expected: 9.0,
      actual: 12.0,
      threshold: 11.25, // 125% of 9h
    },
  },
  
  // Michelle Garcia - Missing Hours (Part-time 24h/week, only worked 16h)
  {
    id: 'pv-013',
    employeeId: 'emp-013',
    employeeName: 'Michelle Garcia',
    weekEnding: '2024-01-19',
    violationType: 'missing-hours',
    severity: 'info',
    message: 'Weekly hours below expected',
    details: {
      expected: 24.0,
      actual: 16.0,
      threshold: 19.2, // 80% of 24h
    },
  },
];

/**
 * Get violations for a specific employee
 */
export function getViolationsForEmployee(employeeId: string): PolicyViolation[] {
  return mockPolicyViolations.filter(v => v.employeeId === employeeId);
}

/**
 * Get violations for a specific week
 */
export function getViolationsForWeek(weekEnding: string): PolicyViolation[] {
  return mockPolicyViolations.filter(v => v.weekEnding === weekEnding);
}

/**
 * Get violations by severity
 */
export function getViolationsBySeverity(severity: 'info' | 'warning' | 'error'): PolicyViolation[] {
  return mockPolicyViolations.filter(v => v.severity === severity);
}

/**
 * Get violations by type
 */
export function getViolationsByType(type: PolicyCheckType): PolicyViolation[] {
  return mockPolicyViolations.filter(v => v.violationType === type);
}

/**
 * Get all violations
 */
export function getAllViolations(): PolicyViolation[] {
  return mockPolicyViolations;
}

/**
 * Get violation summary statistics
 */
export function getViolationSummary() {
  return {
    total: mockPolicyViolations.length,
    byType: {
      dailyOverage: mockPolicyViolations.filter(v => v.violationType === 'daily-overage').length,
      weeklyOverage: mockPolicyViolations.filter(v => v.violationType === 'weekly-overage').length,
      legalMaximumDaily: mockPolicyViolations.filter(v => v.violationType === 'legal-daily-maximum').length,
      legalMaximumWeekly: mockPolicyViolations.filter(v => v.violationType === 'legal-weekly-maximum').length,
      missingHours: mockPolicyViolations.filter(v => v.violationType === 'missing-hours').length,
    },
    bySeverity: {
      info: mockPolicyViolations.filter(v => v.severity === 'info').length,
      warning: mockPolicyViolations.filter(v => v.severity === 'warning').length,
      error: mockPolicyViolations.filter(v => v.severity === 'error').length,
    },
  };
}
